import { ENV } from "./env";
import type {
  AuthData,
  BootstrapData,
  ConfirmData,
  Currency,
  Flow,
  FlowSummary,
  HistoryData,
  Submission,
} from "./types";

interface Envelope<T> {
  data: T;
  requestId: string;
}

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
  requestId?: string;
}

interface AuthRequest {
  client: {
    appVersion: string;
    platform: string;
  };
  initData: string;
  launchToken?: string;
}

export class AppError extends Error {
  code?: string;
  requestId?: string;
  status?: number;
  isNetwork: boolean;

  constructor(message: string, options: { code?: string; isNetwork?: boolean; requestId?: string; status?: number } = {}) {
    super(message);
    this.name = "AppError";
    this.code = options.code;
    this.isNetwork = Boolean(options.isNetwork);
    this.requestId = options.requestId;
    this.status = options.status;
  }
}

function isJsonResponse(response: Response): boolean {
  return response.headers.get("content-type")?.includes("application/json") ?? false;
}

export class ApiClient {
  private accessToken: string | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  private async request<T>(
    path: string,
    options: {
      auth?: boolean;
      body?: unknown;
      idempotent?: boolean;
      method?: "GET" | "POST" | "PATCH";
    } = {},
  ): Promise<T> {
    if (ENV.configError || !ENV.apiOrigin) {
      throw new AppError(ENV.configError ?? "缺少 API 源站配置。", { code: "CONFIG_INVALID" });
    }

    const headers = new Headers({
      Accept: "application/json",
    });

    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json; charset=utf-8");
    }

    if (options.auth) {
      if (!this.accessToken) {
        throw new AppError("会话未建立，请重新从 Bot 打开 Mini App。", { code: "SESSION_INVALID" });
      }

      headers.set("Authorization", `Bearer ${this.accessToken}`);
    }

    if (options.idempotent) {
      headers.set("Idempotency-Key", crypto.randomUUID());
    }

    let response: Response;

    try {
      response = await fetch(`${ENV.apiOrigin}/api/v1${path}`, {
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        headers,
        method: options.method ?? (options.body === undefined ? "GET" : "POST"),
      });
    } catch {
      throw new AppError("网络不可用，请检查与 Worker 源站的连接。", { isNetwork: true });
    }

    const payload = isJsonResponse(response)
      ? ((await response.json()) as Envelope<T> | ErrorEnvelope)
      : null;

    if (!response.ok) {
      const errorPayload = payload as ErrorEnvelope | null;
      throw new AppError(errorPayload?.error?.message ?? "请求失败，请稍后重试。", {
        code: errorPayload?.error?.code,
        requestId: errorPayload?.requestId,
        status: response.status,
      });
    }

    if (!payload || !("data" in payload)) {
      throw new AppError("API 响应缺少 data 包络。", { code: "INVALID_RESPONSE", status: response.status });
    }

    return payload.data;
  }

  authenticate(input: AuthRequest): Promise<AuthData> {
    return this.request<AuthData>("/auth/telegram", {
      body: input,
      method: "POST",
    });
  }

  bootstrap(): Promise<BootstrapData> {
    return this.request<BootstrapData>("/bootstrap", { auth: true });
  }

  createFlow(): Promise<Flow> {
    return this.request<Flow>("/flows", {
      auth: true,
      body: { source: "miniapp" },
      method: "POST",
    });
  }

  updateAmount(flowId: string, amount: string): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}/amount`, {
      auth: true,
      body: { amount },
      method: "PATCH",
    });
  }

  updateCurrency(flowId: string, currency: Currency): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}/currency`, {
      auth: true,
      body: { currency },
      method: "PATCH",
    });
  }

  getSummary(flowId: string): Promise<{ id: string; status: Flow["status"]; summary: FlowSummary }> {
    return this.request<{ id: string; status: Flow["status"]; summary: FlowSummary }>(`/flows/${flowId}/summary`, {
      auth: true,
    });
  }

  confirmFlow(flowId: string): Promise<ConfirmData> {
    return this.request<ConfirmData>(`/flows/${flowId}/confirm`, {
      auth: true,
      body: { source: "miniapp" },
      idempotent: true,
      method: "POST",
    });
  }

  retryFlow(flowId: string): Promise<ConfirmData> {
    return this.request<ConfirmData>(`/flows/${flowId}/retry`, {
      auth: true,
      body: { source: "miniapp" },
      idempotent: true,
      method: "POST",
    });
  }

  cancelFlow(flowId: string): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}/cancel`, {
      auth: true,
      body: {},
      method: "POST",
    });
  }

  getSubmission(submissionId: string): Promise<Submission> {
    return this.request<Submission>(`/submissions/${submissionId}`, {
      auth: true,
    });
  }

  getHistory(cursor?: string | null, limit = 20): Promise<HistoryData> {
    const search = new URLSearchParams();
    search.set("limit", String(limit));
    if (cursor) {
      search.set("cursor", cursor);
    }

    return this.request<HistoryData>(`/history?${search.toString()}`, {
      auth: true,
    });
  }
}
